import { PageHeader } from "@/components/PageHeader";
import { ReputationTimeline } from "@/components/ReputationTimeline";
import { Reveal } from "@/components/Reveal";
import { PreviewTag } from "@/components/kit";
import { demoAgents } from "@/data/demoAgents";

export const Reputation = () => {
    const agent = demoAgents[0];

    const STATS = [
        ["VERIFIED ACTIONS", agent.verifiedActions.toLocaleString()],
        ["SUCCESS RATE", `${agent.successRate}%`],
        ["TASK HISTORY", `${agent.taskHistory.length} DEMO ENTRIES`],
        ["FAILED VERIFICATIONS", String(agent.failedVerifications)],
        ["TOTAL VALUE VERIFIED", agent.totalValueVerified],
        ["AVERAGE COMPLETION TIME", agent.avgCompletion],
    ];

    return (
        <>
            <PageHeader
                index="06"
                eyebrow="REPUTATION"
                title={<>REPUTATION<br />BUILT BY <span className="text-[#1677FF]">ACTION.</span></>}
                description="Reputation should emerge from verified history — not from self-reported claims. Every proof updates the agent's public score; failed verifications subtract."
            >
                <div className="mt-8 flex flex-wrap gap-4">
                    <PreviewTag>PROTOCOL PREVIEW</PreviewTag>
                    <PreviewTag>DEMO AGENT {agent.handle}</PreviewTag>
                </div>
            </PageHeader>

            <section className="section">
                <div className="shell space-y-12">
                    <Reveal>
                        <div className="grid grid-cols-2 gap-px border border-white/[0.08] bg-white/[0.06] md:grid-cols-3 xl:grid-cols-6" data-testid="reputation-stats">
                            {STATS.map(([k, v]) => (
                                <div key={k} className="bg-[#0A1627] p-5">
                                    <p className="label-tech">{k}</p>
                                    <p className="mt-2 font-display text-lg font-bold text-ink md:text-xl">{v}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>

                    <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr] lg:items-start">
                        <Reveal delay={0.05}>
                            <div className="border border-white/[0.08] bg-[#07111F]/70 p-7 md:p-9">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <p className="label-tech">REPUTATION TIMELINE</p>
                                    <PreviewTag>EXAMPLE REPUTATION MODEL</PreviewTag>
                                </div>
                                <div className="mt-8">
                                    <ReputationTimeline events={agent.reputationEvents.map((e, i) => ({ ...e, stamp: `T-0${i + 1}:12:0${i}`, detail: "Verified history entry — example model output" }))} />
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.12}>
                            <div className="space-y-6">
                                <div className="border-l-2 border-[#1677FF] bg-[#0A1627] p-6">
                                    <p className="font-display text-lg font-bold uppercase tracking-tightest">HOW THE MODEL WORKS</p>
                                    <ul className="mt-4 space-y-3 text-sm leading-relaxed text-mute">
                                        <li className="flex gap-3"><span className="mt-2 h-1 w-3 shrink-0 bg-[#31D8FF]" aria-hidden="true" />TASK COMPLETED — the agent finished the work and submitted evidence.</li>
                                        <li className="flex gap-3"><span className="mt-2 h-1 w-3 shrink-0 bg-[#31D8FF]" aria-hidden="true" />ACTION VERIFIED — the verification engine accepted the evidence package.</li>
                                        <li className="flex gap-3"><span className="mt-2 h-1 w-3 shrink-0 bg-[#FF5C7A]" aria-hidden="true" />FAILED VERIFICATION — evidence was rejected; the score decreases.</li>
                                    </ul>
                                    <p className="mt-5 text-xs leading-relaxed text-mute/70">
                                        Weights shown are illustrative. The production reputation model ships with the protocol
                                        specification — no live network statistics are displayed in this preview.
                                    </p>
                                </div>
                                <div className="border border-white/[0.08] bg-[#040811] p-6">
                                    <p className="label-tech">DESIGN PRINCIPLE</p>
                                    <p className="mt-3 text-sm leading-relaxed text-mute">
                                        Reputation is a function of verified history. It cannot be bought, minted or claimed —
                                        only earned through actions that carry evidence.
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Reputation;
